import { useState } from 'react'
import AllRoles from '../../components/AllRoles/AllRoles'
import Pagination from '@/shared/components/Pagination/Pagination'
import { useRoles, useToggleRoleActive } from '@/features/role'
import { Role } from '../../types'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog/ConfirmDialog'
import ActionHeader from '@/shared/components/ActionHeader/ActionHeader'
import { Separator } from '@/ui/separator'
import { Loader } from '@/shared/components/Loader/Loader'
import styles from '../RolePage/Rolepage.module.scss'
import { toast } from 'sonner'

export default function RoleDeletedPage() {

  const [deletedSearch, setDeletedSearch] = useState('')
  const [deletedPage, setDeletedPage] = useState(1)
  const [selectedRole, setSelectedRole] = useState<Role | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pageSize, setPageSize] = useState(10)

  const {
    data: deletedRoles,
    isLoading: isDeletedLoading,
    isError: isDeletedError,
    refetch: refetchDeleted,
  } = useRoles({ is_active: false })

  const toggleRoleActiveMutation = useToggleRoleActive()

  const deletedFiltered = (deletedRoles ?? []).filter((r: Role) => {
    const name = r?.name || ''
    return name.toLowerCase().includes(deletedSearch.toLowerCase())
  })

  const deletedTotalPages = Math.max(1, Math.ceil(deletedFiltered.length / pageSize))
  const deletedDisplayedRoles = deletedFiltered.slice(
    (deletedPage - 1) * pageSize,
    deletedPage * pageSize
  )

  const handleRestore = (role: Role) => {
    setSelectedRole(role)
    setConfirmOpen(true)
  }

  const handleConfirmRestore = () => {
    if (!selectedRole) return
    toggleRoleActiveMutation.mutate(selectedRole.id, {
      onSuccess: () => {
        toast.success('Role restored successfully')
      },
      onError: (error: any) => {
        const apiMessage =
          error?.response?.data?.message ||
          error?.message ||
          'Unable to restore role'
        toast.error(apiMessage)
      },
    })
    setConfirmOpen(false)
    setSelectedRole(null)
  }

  return (
    <div>
      {isDeletedLoading ? (
        <Loader text="Loading deleted roles..." />
      ) : isDeletedError ? (
        <p className={styles.error}>Error loading deleted roles</p>
      ) : (
        <>
          <div className={styles.actionHeaderWrapper}>
            <ActionHeader
              title="Deleted Roles"
              search={deletedSearch}
              onSearchChange={setDeletedSearch}
              searchPlaceholder="Search Roles"
              actions={[
                {
                  icon: 'refresh',
                  onClick: () => refetchDeleted(),
                  variant: 'icon',
                  ariaLabel: 'Refresh',
                  key: 'refresh-deleted-roles',
                },
              ]}
            />
          </div>

          <Separator />

          <div className={styles.tableWrapper}>
            <AllRoles
              mode="deleted"
              roles={deletedDisplayedRoles}
              onRestore={handleRestore}
            />

            {deletedFiltered.length > 0 && (
              <Pagination
                currentPage={deletedPage}
                totalPages={deletedTotalPages}
                onPageChange={setDeletedPage}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(size) => {
                  setPageSize(size)
                  setDeletedPage(1)
                }}
              />
            )}
          </div>
        </>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Restore role"
        message={
          selectedRole
            ? `Restore role ${selectedRole.name} back to active roles?`
            : 'Restore this role?'
        }
        confirmText="Restore"
        cancelText="Cancel"
        onConfirm={handleConfirmRestore}
        onCancel={() => {
          setConfirmOpen(false)
          setSelectedRole(null)
        }}
      />
    </div>
  )
}
